import {
  useQuery as _useQuery,
  type DefaultError,
  type QueryKey,
  type QueryClient,
  type UseQueryOptions,
  type UseQueryResult,
  type DefinedUseQueryResult,
} from '@tanstack/react-query';
import type { DefinedInitialDataOptions, UndefinedInitialDataOptions } from '@tanstack/react-query';

/**
 * When `TQueryFnData` includes `undefined`, this resolves to an object that
 * overrides the `queryFn` property with an impossible string-literal type,
 * producing a clear compile-time error at the call site.
 *
 * TanStack Query v5 throws at runtime when `queryFn` returns `undefined`,
 * but the library types don't enforce this. This guard closes that gap.
 *
 * Skips the check when `TQueryFnData` is `unknown` (default when no
 * `queryFn` is provided, e.g. disabled queries or placeholder usage).
 */
type AssertQueryFnDefined<TQueryFnData> = [unknown] extends [TQueryFnData]
  ? {}
  : [undefined] extends [TQueryFnData]
    ? {
        queryFn: 'Error: queryFn must not return a type that includes undefined — TanStack Query v5 throws at runtime for undefined query data';
      }
    : {};

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> & AssertQueryFnDefined<TQueryFnData>,
  queryClient?: QueryClient
): DefinedUseQueryResult<NoInfer<TData>, TError>;

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> & AssertQueryFnDefined<TQueryFnData>,
  queryClient?: QueryClient
): UseQueryResult<NoInfer<TData>, TError>;

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & AssertQueryFnDefined<TQueryFnData>,
  queryClient?: QueryClient
): UseQueryResult<NoInfer<TData>, TError>;

export function useQuery(options: UseQueryOptions, queryClient?: QueryClient) {
  return _useQuery(options, queryClient);
}
